import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Loader2, Truck, Search } from 'lucide-react';
import productionService from '../../services/productionService';
import purchaseService from '../../services/purchaseService';
import subcontractService from '../../services/subcontractService';

function BatchCreateSubcontractDialog({ open, onOpenChange, onSuccess }) {
  // ========== Loading States ==========
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ========== Data ==========
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // ========== Selection States ==========
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [selectedStepKeys, setSelectedStepKeys] = useState(new Set());
  const [orderSearch, setOrderSearch] = useState('');
  const [stepFilter, setStepFilter] = useState(''); // '' = all, otherwise stepName

  // ========== Form ==========
  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    unit_price: '',
    expected_return_date: '',
    notes: ''
  });

  // ========== Reset on open ==========
  useEffect(() => {
    if (open) {
      setSelectedOrderIds(new Set());
      setSelectedStepKeys(new Set());
      setOrderSearch('');
      setStepFilter('');
      setError(null);
      setFormData({
        supplier_id: '',
        supplier_name: '',
        unit_price: '',
        expected_return_date: '',
        notes: ''
      });
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      loadOrders();
      loadSuppliers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadOrders = useCallback(async () => {
    try {
      setLoadingOrders(true);
      const response = await productionService.getProductionOrders({
        status: 'in_progress',
        page_size: 200
      });
      const items = response.data?.items || response.items || [];
      setOrders(items);
    } catch (err) {
      console.error('Failed to load production orders:', err);
      setError('加载工单列表失败');
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const loadSuppliers = useCallback(async () => {
    try {
      setLoadingSuppliers(true);
      const result = await purchaseService.getSuppliers(1, 200);
      const list = Array.isArray(result) ? result : (result?.data?.items || result?.items || []);
      setSuppliers(list);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  }, []);

  // ========== Derived: Available Steps from Selected Orders ==========
  const availableSteps = useMemo(() => {
    const steps = [];
    const selectedOrders = orders.filter(o => selectedOrderIds.has(o._id));

    selectedOrders.forEach(order => {
      const items = order.items || [];
      items.forEach((item, itemIndex) => {
        const processSteps = item.process_steps || [];
        processSteps.forEach(step => {
          const availableQty = (step.quantity_pending || 0) - (step.subcontract_quantity || 0);
          if (availableQty > 0 && step.status !== 'completed') {
            // Previous step constraint
            if (step.sequence > 1) {
              const prevStep = processSteps.find(s => s.sequence === step.sequence - 1);
              if (prevStep && prevStep.status !== 'completed') return;
            }
            steps.push({
              key: `${order._id}-${itemIndex}-${step.sequence}`,
              orderId: order._id,
              orderNumber: order.production_order_id,
              itemIndex,
              stepSequence: step.sequence,
              stepName: step.name,
              finishedProductCode: item.finished_product_code,
              finishedProductName: item.finished_product_name,
              plannedQuantity: item.planned_quantity,
              availableQuantity: availableQty,
              quantityCompleted: step.quantity_completed || 0,
            });
          }
        });
      });
    });

    return steps;
  }, [orders, selectedOrderIds]);

  // ========== Derived: Step Name Tags ==========
  const stepNameStats = useMemo(() => {
    const stats = {};
    availableSteps.forEach(s => {
      if (!stats[s.stepName]) {
        stats[s.stepName] = { count: 0, totalQty: 0, keys: [] };
      }
      stats[s.stepName].count += 1;
      stats[s.stepName].totalQty += s.availableQuantity;
      stats[s.stepName].keys.push(s.key);
    });
    return Object.entries(stats).map(([name, data]) => ({ name, ...data }));
  }, [availableSteps]);

  // ========== Derived: Filtered Steps ==========
  const filteredSteps = useMemo(() => {
    if (!stepFilter) return availableSteps;
    return availableSteps.filter(s => s.stepName === stepFilter);
  }, [availableSteps, stepFilter]);

  // ========== Derived: Selected Step Name ==========
  const selectedStepName = useMemo(() => {
    if (selectedStepKeys.size === 0) return null;
    const first = availableSteps.find(s => selectedStepKeys.has(s.key));
    return first?.stepName || null;
  }, [availableSteps, selectedStepKeys]);

  // ========== Derived: Filtered Orders ==========
  const filteredOrders = useMemo(() => {
    if (!orderSearch.trim()) return orders;
    const term = orderSearch.toLowerCase();
    return orders.filter(o => {
      const poId = (o.production_order_id || '').toLowerCase();
      const code = (o.finished_product_code || '').toLowerCase();
      const name = (o.finished_product_name || '').toLowerCase();
      return poId.includes(term) || code.includes(term) || name.includes(term);
    });
  }, [orders, orderSearch]);

  // ========== Handlers ==========
  const toggleOrder = (orderId) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
    // Clear step selection and filter when orders change to avoid stale selections
    setSelectedStepKeys(new Set());
    setStepFilter('');
  };

  const toggleStep = (step) => {
    setSelectedStepKeys(prev => {
      const next = new Set(prev);
      if (next.has(step.key)) {
        next.delete(step.key);
        return next;
      }
      // Enforce same step name
      if (next.size > 0) {
        const firstKey = Array.from(next)[0];
        const firstStep = availableSteps.find(s => s.key === firstKey);
        if (firstStep && firstStep.stepName !== step.stepName) {
          setError(`只能选择同名工序。当前已选"${firstStep.stepName}"，不能混选"${step.stepName}"`);
          return prev;
        }
      }
      next.add(step.key);
      setError(null);
      return next;
    });
  };

  // ========== Step Tag Filter ==========
  const handleStepTagClick = (tagName) => {
    if (stepFilter === tagName) {
      // Toggle off
      setStepFilter('');
    } else {
      // Switch to this tag, and if user has already selected steps of a different name, warn
      if (selectedStepKeys.size > 0) {
        const firstKey = Array.from(selectedStepKeys)[0];
        const firstStep = availableSteps.find(s => s.key === firstKey);
        if (firstStep && firstStep.stepName !== tagName) {
          setError(`已选工序为"${firstStep.stepName}"，与"${tagName}"不同名。请先取消已选工序再切换。`);
          return;
        }
      }
      setStepFilter(tagName);
      setError(null);
    }
  };

  // ========== Select All / None for filtered steps ==========
  const allFilteredSelected = filteredSteps.length > 0 && filteredSteps.every(s => selectedStepKeys.has(s.key));
  const someFilteredSelected = filteredSteps.some(s => selectedStepKeys.has(s.key)) && !allFilteredSelected;

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      // Deselect all filtered
      setSelectedStepKeys(prev => {
        const next = new Set(prev);
        filteredSteps.forEach(s => next.delete(s.key));
        return next;
      });
    } else {
      // Select all filtered (enforce same step name)
      if (filteredSteps.length === 0) return;
      const targetName = filteredSteps[0].stepName;

      setSelectedStepKeys(prev => {
        // If already has selections of a different name, replace them
        const next = new Set();
        // Keep existing selections of the same name
        prev.forEach(key => {
          const s = availableSteps.find(x => x.key === key);
          if (s && s.stepName === targetName) {
            next.add(key);
          }
        });
        // Add all filtered
        filteredSteps.forEach(s => next.add(s.key));
        return next;
      });
      setError(null);
    }
  };

  const handleSupplierChange = (value) => {
    const supplier = suppliers.find(s => s._id === value || s.supplier_id === value);
    setFormData(prev => ({
      ...prev,
      supplier_id: value,
      supplier_name: supplier?.supplier_name || supplier?.name || ''
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.supplier_id) {
      setError('请选择供应商');
      return;
    }
    if (selectedStepKeys.size === 0) {
      setError('请至少选择一个工序');
      return;
    }

    const selectedItems = availableSteps.filter(s => selectedStepKeys.has(s.key));
    const stepName = selectedItems[0]?.stepName;
    const stepSequence = selectedItems[0]?.stepSequence;

    setSubmitting(true);
    setError(null);

    try {
      const items = selectedItems.map(s => ({
        production_order_id: s.orderId,
        production_order_number: s.orderNumber,
        item_index: s.itemIndex,
        finished_product_code: s.finishedProductCode,
        finished_product_name: s.finishedProductName,
        planned_quantity: s.availableQuantity,
        unit_price: parseFloat(formData.unit_price) || 0,
        notes: formData.notes
      }));

      const data = {
        supplier_id: formData.supplier_id,
        supplier_name: formData.supplier_name,
        step_name: stepName,
        step_sequence: stepSequence,
        items,
        expected_return_date: formData.expected_return_date,
        notes: formData.notes
      };

      const result = await subcontractService.createSubcontractOrder(data);

      if (onSuccess) onSuccess(result);
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to create batch subcontract:', err);
      setError(err.response?.data?.error || err.message || '批量创建外协单失败');
    } finally {
      setSubmitting(false);
    }
  };

  // ========== Stats ==========
  const totalPlannedQty = useMemo(() => {
    return availableSteps
      .filter(s => selectedStepKeys.has(s.key))
      .reduce((sum, s) => sum + s.availableQuantity, 0);
  }, [availableSteps, selectedStepKeys]);

  const totalEstimatedAmount = useMemo(() => {
    const price = parseFloat(formData.unit_price) || 0;
    return totalPlannedQty * price;
  }, [totalPlannedQty, formData.unit_price]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-amber-600" />
            批量创建外协单
          </DialogTitle>
          <DialogDescription>
            选择多个进行中的工单，勾选同名工序后统一创建外协单
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          {/* Step 1: Select Orders */}
          <div className="space-y-2">
            <Label className="text-base font-medium">1. 选择工单 ({selectedOrderIds.size} 个已选)</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索工单号、产品代码或名称..."
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                className="pl-9"
                disabled={loadingOrders}
              />
            </div>

            <div className="rounded-md border max-h-48 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">选择</TableHead>
                    <TableHead>工单号</TableHead>
                    <TableHead>产品</TableHead>
                    <TableHead className="text-center">计划数量</TableHead>
                    <TableHead className="text-center">状态</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingOrders ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                        暂无进行中的工单
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map(order => (
                      <TableRow
                        key={order._id}
                        className={selectedOrderIds.has(order._id) ? 'bg-amber-50' : ''}
                        onClick={() => toggleOrder(order._id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedOrderIds.has(order._id)}
                            onChange={() => toggleOrder(order._id)}
                            className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                        </TableCell>
                        <TableCell className="font-medium">{order.production_order_id}</TableCell>
                        <TableCell>
                          <div className="font-medium">{order.finished_product_code}</div>
                          <div className="text-xs text-muted-foreground">{order.finished_product_name}</div>
                        </TableCell>
                        <TableCell className="text-center">{order.total_planned_quantity}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="default">进行中</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Step 2: Select Steps */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">
                2. 选择工序
                {selectedStepName && (
                  <Badge variant="outline" className="ml-2 text-amber-700 border-amber-300 bg-amber-50">
                    {selectedStepName}
                  </Badge>
                )}
              </Label>
              <span className="text-sm text-muted-foreground">
                {selectedStepKeys.size} 个已选，共 {totalPlannedQty} 件
              </span>
            </div>

            {/* Step Name Tag Filters */}
            {stepNameStats.length > 0 && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-muted-foreground">按工序筛选:</span>
                <button
                  onClick={() => setStepFilter('')}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    stepFilter === ''
                      ? 'bg-slate-800 text-white border-slate-800'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                >
                  全部 ({availableSteps.length})
                </button>
                {stepNameStats.map(tag => {
                  const isSelected = stepFilter === tag.name;
                  const hasSelectedInTag = tag.keys.some(k => selectedStepKeys.has(k));
                  return (
                    <button
                      key={tag.name}
                      onClick={() => handleStepTagClick(tag.name)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        isSelected
                          ? 'bg-amber-600 text-white border-amber-600'
                          : hasSelectedInTag
                          ? 'bg-amber-50 text-amber-700 border-amber-300 hover:border-amber-400'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                      }`}
                      title={`${tag.count} 个工序，共 ${tag.totalQty} 件`}
                    >
                      {tag.name} ({tag.count})
                    </button>
                  );
                })}
              </div>
            )}

            <div className="rounded-md border max-h-64 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      {filteredSteps.length > 0 && (
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someFilteredSelected;
                          }}
                          onChange={toggleSelectAllFiltered}
                          className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                      )}
                    </TableHead>
                    <TableHead>工单号</TableHead>
                    <TableHead>产品</TableHead>
                    <TableHead>工序</TableHead>
                    <TableHead className="text-center">已完成</TableHead>
                    <TableHead className="text-center">可外协</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedOrderIds.size === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-4">
                        请先选择工单
                      </TableCell>
                    </TableRow>
                  ) : availableSteps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-4">
                        所选工单中暂无可外协的工序
                      </TableCell>
                    </TableRow>
                  ) : filteredSteps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-4">
                        当前筛选条件下无匹配工序
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSteps.map(step => {
                      const isSelected = selectedStepKeys.has(step.key);
                      const isDisabled = selectedStepKeys.size > 0 && !isSelected && selectedStepName !== step.stepName;
                      return (
                        <TableRow
                          key={step.key}
                          className={isSelected ? 'bg-amber-50' : isDisabled ? 'opacity-50' : ''}
                          onClick={() => !isDisabled && toggleStep(step)}
                          style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleStep(step)}
                              disabled={isDisabled}
                              className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                          </TableCell>
                          <TableCell className="font-medium">{step.orderNumber}</TableCell>
                          <TableCell>
                            <div className="font-medium">{step.finishedProductCode}</div>
                            <div className="text-xs text-muted-foreground">{step.finishedProductName}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{step.stepName}</Badge>
                          </TableCell>
                          <TableCell className="text-center">{step.quantityCompleted}</TableCell>
                          <TableCell className="text-center font-semibold text-amber-700">
                            {step.availableQuantity}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Step 3: Form */}
          <div className="space-y-2">
            <Label className="text-base font-medium">3. 填写外协信息</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="batch-supplier">供应商 <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={handleSupplierChange}
                  disabled={loadingSuppliers || submitting}
                >
                  <SelectTrigger id="batch-supplier">
                    <SelectValue placeholder={loadingSuppliers ? '加载中...' : '选择供应商'} />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(supplier => (
                      <SelectItem
                        key={supplier._id || supplier.supplier_id}
                        value={supplier._id || supplier.supplier_id}
                      >
                        {supplier.supplier_name || supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="batch-unit-price">加工单价 (元/件)</Label>
                <Input
                  id="batch-unit-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => handleChange('unit_price', e.target.value)}
                  placeholder="0.00"
                  disabled={submitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="batch-return-date">计划交期</Label>
                <Input
                  id="batch-return-date"
                  type="date"
                  value={formData.expected_return_date}
                  onChange={(e) => handleChange('expected_return_date', e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="batch-notes">备注</Label>
                <Input
                  id="batch-notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="外协备注..."
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          {selectedStepKeys.size > 0 && (
            <div className="text-sm bg-gray-50 p-3 rounded border">
              <div className="font-medium mb-1">汇总</div>
              <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                <div>已选工单: {new Set(availableSteps.filter(s => selectedStepKeys.has(s.key)).map(s => s.orderId)).size} 个</div>
                <div>已选工序: {selectedStepKeys.size} 个</div>
                <div>总外协数量: {totalPlannedQty} 件</div>
                {formData.unit_price && (
                  <div>预计金额: {totalEstimatedAmount.toFixed(2)} 元</div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !formData.supplier_id || selectedStepKeys.size === 0}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                创建中...
              </>
            ) : (
              <>创建外协单 ({selectedStepKeys.size})</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BatchCreateSubcontractDialog;
